import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cachedGet, publicApi } from "@/lib/publicApi";
import { formatApiError } from "@/lib/api";
import { EmergencyBar } from "./SaudeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const PERIODS = [
  ["morning", "Manhã"],
  ["afternoon", "Tarde"],
  ["evening", "Início da noite"],
];
const CHANNELS = [
  ["phone", "Telefone"],
  ["whatsapp", "WhatsApp"],
  ["email", "E-mail"],
];

export default function EvaluationRequest() {
  const idempotencyKey = useRef(`req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const [treatments, setTreatments] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    phone_has_whatsapp: false,
    email: "",
    city: "",
    state: "",
    for_self: true,
    relationship: "",
    interest_topic: "",
    preferred_channel: "phone",
    preferred_period: "morning",
    message: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [consented, setConsented] = useState(false);
  const [ackEmergency, setAckEmergency] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await cachedGet("/treatments");
        setTreatments(data);
        setForm((f) => ({ ...f, interest_topic: f.interest_topic || data[0]?.title || "Não sei ainda" }));
      } catch {
        setTreatments([]);
      }
    })();
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || result) return;
    setSubmitting(true);
    try {
      const { data } = await publicApi.post("/evaluation-requests", {
        ...form,
        email: form.email || null,
        idempotency_key: idempotencyKey.current,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSubmitting(false);
  };

  if (result)
    return (
      <div data-testid="evaluation-success" className="space-y-5">
        <CheckCircle2 size={26} className="text-success" />
        <h1 className="font-display text-2xl font-medium tracking-tight text-ink-900">Solicitação enviada</h1>
        <p className="text-sm leading-relaxed text-ink-600">{result.message}</p>
        <div className="er-card">
          <p className="er-label">Protocolo</p>
          <code data-testid="evaluation-protocol" className="mt-1 block text-sm text-ink-900">
            {result.protocol}
          </code>
        </div>
        <p className="text-xs text-ink-400">
          Não informamos um tempo exato de retorno. Se houver risco imediato, use os canais de emergência.
        </p>
        <EmergencyBar />
      </div>
    );

  return (
    <div data-testid="evaluation-request-page" className="space-y-6">
      <Link to="/saude/fale-com-a-erimus" className="inline-flex items-center gap-2 text-sm text-ink-500" data-testid="evaluation-back">
        <ArrowLeft size={16} /> Voltar
      </Link>

      <div>
        <p className="er-label">Solicitar uma avaliação</p>
        <h1 className="mt-3 font-display text-2xl font-medium tracking-tight text-ink-900">Vamos entrar em contato</h1>
        <p data-testid="evaluation-identified-notice" className="mt-3 rounded-2xl bg-sand/70 p-4 text-sm leading-relaxed text-ink-600">
          Este fluxo é identificado, porque a Erimus precisará falar com você. Não pedimos CPF, documentos, histórico
          clínico, medicamentos nem dados financeiros.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label htmlFor="full_name" className="text-sm text-ink-600">Nome completo</Label>
          <Input id="full_name" data-testid="evaluation-name-input" value={form.full_name} onChange={set("full_name")} className="mt-2 h-12 rounded-xl bg-white" required />
        </div>
        <div>
          <Label htmlFor="phone" className="text-sm text-ink-600">Telefone com DDD</Label>
          <Input id="phone" data-testid="evaluation-phone-input" inputMode="tel" value={form.phone} onChange={set("phone")} className="mt-2 h-12 rounded-xl bg-white" required />
        </div>
        <label className="flex items-center gap-3 text-sm text-ink-600">
          <Checkbox data-testid="evaluation-whatsapp-checkbox" checked={form.phone_has_whatsapp} onCheckedChange={(v) => setForm({ ...form, phone_has_whatsapp: !!v })} />
          Este número tem WhatsApp
        </label>
        <div>
          <Label htmlFor="email" className="text-sm text-ink-600">E-mail (opcional)</Label>
          <Input id="email" data-testid="evaluation-email-input" type="email" value={form.email} onChange={set("email")} className="mt-2 h-12 rounded-xl bg-white" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="city" className="text-sm text-ink-600">Cidade</Label>
            <Input id="city" data-testid="evaluation-city-input" value={form.city} onChange={set("city")} className="mt-2 h-12 rounded-xl bg-white" required />
          </div>
          <div>
            <Label htmlFor="state" className="text-sm text-ink-600">Estado</Label>
            <Input id="state" data-testid="evaluation-state-input" value={form.state} onChange={set("state")} className="mt-2 h-12 rounded-xl bg-white" required />
          </div>
        </div>

        <div>
          <Label className="text-sm text-ink-600">A solicitação é para</Label>
          <div className="mt-2 flex gap-2">
            {[["self", "Para mim"], ["other", "Para outra pessoa"]].map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-testid={`evaluation-for-${value}`}
                onClick={() => setForm({ ...form, for_self: value === "self" })}
                className={`min-h-[44px] flex-1 rounded-full text-sm transition-colors ${
                  form.for_self === (value === "self") ? "bg-sage-600 text-bone" : "bg-sand text-ink-600"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {!form.for_self ? (
          <div>
            <Label htmlFor="relationship" className="text-sm text-ink-600">Sua relação com essa pessoa</Label>
            <Input id="relationship" data-testid="evaluation-relationship-input" value={form.relationship} onChange={set("relationship")} className="mt-2 h-12 rounded-xl bg-white" required />
          </div>
        ) : null}

        <div>
          <Label htmlFor="interest" className="text-sm text-ink-600">Tratamento ou tema de interesse</Label>
          <select id="interest" data-testid="evaluation-topic-select" value={form.interest_topic} onChange={set("interest_topic")} className="mt-2 h-12 w-full rounded-xl border border-border bg-white px-3 text-sm">
            {treatments.map((t) => (
              <option key={t.slug} value={t.title}>{t.title}</option>
            ))}
            <option value="Não sei ainda">Não sei ainda</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="channel" className="text-sm text-ink-600">Canal preferido</Label>
            <select id="channel" data-testid="evaluation-channel-select" value={form.preferred_channel} onChange={set("preferred_channel")} className="mt-2 h-12 w-full rounded-xl border border-border bg-white px-3 text-sm">
              {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="period" className="text-sm text-ink-600">Melhor período</Label>
            <select id="period" data-testid="evaluation-period-select" value={form.preferred_period} onChange={set("preferred_period")} className="mt-2 h-12 w-full rounded-xl border border-border bg-white px-3 text-sm">
              {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="message" className="text-sm text-ink-600">Mensagem (opcional)</Label>
          <Textarea id="message" data-testid="evaluation-message-input" value={form.message} onChange={set("message")} rows={3} className="mt-2 rounded-xl bg-white" />
        </div>

        <label className="flex items-start gap-3 text-sm text-ink-600">
          <Checkbox data-testid="evaluation-privacy-checkbox" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} />
          Li e aceito a política de privacidade.
        </label>
        <label className="flex items-start gap-3 text-sm text-ink-600">
          <Checkbox data-testid="evaluation-consent-checkbox" checked={consented} onCheckedChange={(v) => setConsented(!!v)} />
          Autorizo a Erimus a entrar em contato comigo pelos dados informados.
        </label>
        <label className="flex items-start gap-3 text-sm text-ink-600">
          <Checkbox data-testid="evaluation-emergency-checkbox" checked={ackEmergency} onCheckedChange={(v) => setAckEmergency(!!v)} />
          Entendi que este canal não atende emergências.
        </label>

        <Button
          type="submit"
          data-testid="evaluation-submit"
          disabled={submitting || !accepted || !consented || !ackEmergency}
          className="h-12 w-full rounded-full bg-sage-600 text-white hover:bg-sage-700"
        >
          {submitting ? "Enviando…" : "Enviar solicitação"}
        </Button>
      </form>
      <EmergencyBar />
    </div>
  );
}
